
import { createRow, MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useParams } from "react-router";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import RichTextEditor from "../../RichTextEditor/RichTextEditor";

const FuelOilConsMeasurmentMethod = ({ fuelOilConsumptionMeasurementMethod, preFilldata, refetchMonitoringData, vesselMstId }) => {

    const [activeAction, setactiveAction] = useState(-1);
    const [validationErrors, setValidationErrors] = useState({});
    const [fuelConsData, setFuelConsData] = useState([]);
    const [error, setError] = useState("");
    const editorRef = useRef();
    const RowActions = {
        Edit: 0,
        Add: 1,
    };

    useEffect(() => {
        if (fuelOilConsumptionMeasurementMethod || preFilldata) {
            const row = {
                method: preFilldata?.method || fuelOilConsumptionMeasurementMethod?.method || "",
                description: fuelOilConsumptionMeasurementMethod?.description || "",
            };
            setFuelConsData([row]);
        } else {
            setFuelConsData([]);
        }
    }, [fuelOilConsumptionMeasurementMethod, preFilldata]);

    const resetActiveRow = () => {
        setactiveAction(-1);
        setValidationErrors({});
        setError("");
    };
    const handleUpdateFuelConsCollectionPlan = async ({ values, table, row }) => {
        const editorContent = editorRef.current?.getEditor()?.root.innerHTML || values.description;
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = editorContent;

        const plainText = tempDiv.textContent?.trim() || "";
        const hasImage = tempDiv.querySelector("img") !== null;
        if (!plainText && !hasImage) {
            setError("Field is required");
            return;
        }
        const payload = {
            method: values?.method,
            description: editorContent,
        };

        try {
            const fuelConsumptionData = await callApi({
                method: "POST",
                url: API_ROUTES.FuelOilConsumptionMethod(vesselMstId),
                data: payload,
            });
            if (fuelConsumptionData?.isSuccess) {
                emitToasterMsg("success", "Table  Method to measure fuel oil consumption", "has been updated successfully");
                table.setEditingRow(null);
                setactiveAction(-1);
                setFuelConsData(prev =>
                    prev.map((item, index) =>
                        index === row.index ? { ...item, description: editorContent } : item
                    )
                );
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table  Method to measure fuel oil consumption", fuelConsumptionData?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table  Method to measure fuel oil consumption", error.errorMessage);
        }
    };
    const handleFuelConsMeasurementMethod = (table, row, action) => {
        if (activeAction !== -1) return;

        if (action === RowActions.Add) {
            const defaultRow = createRow(table, {
                method: ''
            });
            table.setCreatingRow(defaultRow);
            setactiveAction(action);
            return;
        }
        if (action === RowActions.Edit) {
            table.setEditingRow(row);
            setactiveAction(action);
        }
    };
    const columns = useMemo(
        () => [

            {
                accessorKey: "method",
                header: "Method",
                enableEditing: false,
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle",
                    },
                },
                muiEditTextFieldProps: {
                    inputProps: {
                        maxLength: 255,
                    },

                },
                Cell: ({ cell }) => (
                    <Tooltip
                        title={
                            <span>
                                Value auto-filled. To update, please modify the details in{" "}
                                <a
                                    href={`/my-fleet/enroll-vessel/${vesselMstId}`}
                                   style={{ color: "blue", textDecoration: "underline" }}
                                >
                                    MyFleet
                                </a>
                            </span>
                        }
                        placement="bottom-start"
                        arrow
                    >
                        <span>{cell.getValue()}</span>
                    </Tooltip>
                ),
            },
            {
                accessorKey: "description",
                header: "Description",
                size: 400,
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle",
                    },
                },

                Cell: ({ cell }) => (
                    <div
                        dangerouslySetInnerHTML={{ __html: cell.getValue() }}
                        style={{ width: '100%' }}
                    />
                ),
                Edit: ({ cell, column, row, table }) => (
                    <div style={{ width: "100%" }}>
                        <RichTextEditor
                            ref={editorRef}
                            value={row.original.description || ""}
                            onChange={(value) => {
                                row._valuesCache[column.id] = value;
                                table.setEditingRow(row);
                                setError("");
                            }}
                            style={{ height: '100%' }}
                        />
                        {error && (
                            <div style={{ color: "red", fontSize: "0.9rem", marginTop: "0.25rem" }}>
                                {error}
                            </div>
                        )}
                    </div>
                ),


            }
        ], [error]
    )


    const table = useMaterialReactTable({
        columns,
        data: fuelConsData || [],
        enableStickyHeader: true,
        enableSorting: false,
        enableRowSelection: false,
        enableColumnPinning: false,
        enableTopToolbar: false,
        enableBottomToolbar: false,
        enableColumnActions: false,
        createDisplayMode: "row",
        editDisplayMode: "row",
        enableEditing: true,
        enablePagination: false,
        muiTableHeadCellProps: {
            sx: {
                color: "rgb(255 255 255)",
                backgroundColor: "#042a4c",
                position: "sticky",
                borderRight: "1px solid lightgray",
                "&:last-child": {
                    borderRight: "none",
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: "520px",
            },
        },
        displayColumnDefOptions: {
            'mrt-row-actions': {
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                    },
                },
            },
        },
        muiTableBodyCellProps: ({ row, cell }) => {
            return {
                sx: {
                    borderRight: "1px solid lightgray",
                    marginTop: '0 !important',
                    "&:last-child": {
                        borderRight: "none",
                    },
                    color: "",
                },
            };
        },
        muiTableBodyRowProps: ({ row }) => ({
            sx: {
                "&:not(:last-of-type)": {
                    borderBottom: "1px solid lightgray",
                },
                verticalAlign: "top",
            },
        }),
        muiTableHeadRowProps: {
            sx: {
                borderBottom: "2px solid lightgray",
            },
        },
        columnOrder: [
            "method",
            "description",
        ],
        onEditingRowCancel: resetActiveRow,
        onCreatingRowCancel: resetActiveRow,
        onEditingRowSave: handleUpdateFuelConsCollectionPlan,
        renderRowActions: ({ row, table }) => {
            return (
                <Box sx={{ display: "flex", gap: "2rem" }}>
                    <Box>
                        <Tooltip title="Edit">
                            <IconButton
                                style={{ padding: "0px" }}
                                onClick={() =>
                                    handleFuelConsMeasurementMethod(
                                        table,
                                        row,
                                        RowActions.Edit
                                    )
                                }
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>

                </Box>
            );
        },
    });


    return (

        <div className="fuel-registry-b3 mt-1">
            <div style={{ marginBottom: "1rem", fontSize: '16px' }}>
                i.The applied method for measurement of total fuel consumption for this ship is given
                below. The description explains the procedure for measuring data and calculating
                annual values, measurement equipment involved, etc
            </div>
            <MaterialReactTable table={table} />
        </div>
    );

}
export default FuelOilConsMeasurmentMethod;